import { FC, ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Col, Flex, Grid, Popover, Row, Tree, Typography } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { TbChartInfographic, TbH1, TbH2, TbH3, TbH4, TbH5, TbH6, TbListDetails } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import { EmptySection } from '@/shared';
import { ellipsisStyling } from '@/utilities';

import { scanTableOfContents, TableOfContentsItem } from './scanner';

type MemoTableOfContentsPlacement = 'drawer' | 'fixed' | 'sticky';

const DEFAULT_POPOVER_WIDTH = 360;
const MIN_POPOVER_WIDTH = 180;
const MARKER_WIDTH = 24;
const MARKER_GAP = 12;
const TRIGGER_WIDTH = MARKER_WIDTH + 8;
const VIEWPORT_GUTTER = 12;
export type MemoTableOfContentsItem = TableOfContentsItem;

export interface MemoTableOfContentsProps {
    contentRef: RefObject<HTMLElement | null>;
    excludeHidden?: boolean;
    getHeadingIcon?: (item: MemoTableOfContentsItem) => ReactNode;
    maxContentWidth?: number;
    onSelect?: () => void;
    placement?: MemoTableOfContentsPlacement;
    refreshKey?: string;
}

const headingIconMap = {
    1: TbH1,
    2: TbH2,
    3: TbH3,
    4: TbH4,
    5: TbH5,
    6: TbH6,
} as Record<number, typeof TbH1>;

const getScrollParent = (element?: HTMLElement | null): HTMLElement | Window => {
    let parent = element?.parentElement;

    while (parent) {
        const style = window.getComputedStyle(parent);
        const overflow = `${style.overflow}${style.overflowY}${style.overflowX}`;

        if (/(auto|scroll)/.test(overflow)) {
            return parent;
        }

        parent = parent.parentElement;
    }

    return window;
};

const tableOfContentsFingerprint = (items: MemoTableOfContentsItem[]) =>
    items
        .map((item) =>
            [
                item.id,
                item.key,
                item.level,
                item.reportComponentId,
                item.reportComponentType,
                item.title,
                item.type,
            ].join(':'),
        )
        .join('|');

const MemoTableOfContents: FC<MemoTableOfContentsProps> = ({
    contentRef,
    excludeHidden = false,
    getHeadingIcon,
    maxContentWidth = 960,
    onSelect,
    placement = 'sticky',
    refreshKey,
}) => {
    const [items, setItems] = useState<MemoTableOfContentsItem[]>([]);
    const [activeItemId, setActiveItemId] = useState<string>();
    const [hoveredItemId, setHoveredItemId] = useState<string>();
    const [fixedPosition, setFixedPosition] = useState({
        left: VIEWPORT_GUTTER,
        popoverWidth: DEFAULT_POPOVER_WIDTH,
    });
    const animationFrameRef = useRef<number | null>(null);
    const itemsFingerprintRef = useRef('');
    const refreshTimeoutRef = useRef<number | null>(null);
    const screens = Grid.useBreakpoint();

    const { token } = useAntDesignTheme();
    const { reportComponentTypeToIconMap } = useMaps();

    const refreshItems = useCallback(
        (options?: { immediate?: boolean }) => {
            const runRefresh = () => {
                if (animationFrameRef.current) {
                    window.cancelAnimationFrame(animationFrameRef.current);
                }

                animationFrameRef.current = window.requestAnimationFrame(() => {
                    const nextItems = scanTableOfContents(contentRef.current, { excludeHidden });
                    const nextFingerprint = tableOfContentsFingerprint(nextItems);

                    if (itemsFingerprintRef.current === nextFingerprint) {
                        return;
                    }

                    itemsFingerprintRef.current = nextFingerprint;
                    setItems(nextItems);
                });
            };

            if (options?.immediate) {
                runRefresh();
                return;
            }

            if (refreshTimeoutRef.current) {
                window.clearTimeout(refreshTimeoutRef.current);
            }

            refreshTimeoutRef.current = window.setTimeout(runRefresh, 150);
        },
        [contentRef, excludeHidden],
    );

    const scheduleRefreshItems = useCallback(() => {
        refreshItems();
    }, [refreshItems]);

    const updateFixedPosition = useCallback(() => {
        if (placement !== 'fixed') {
            return;
        }

        const root = contentRef.current;
        const scrollParent = getScrollParent(root);
        const viewportLeft = scrollParent === window ? 0 : (scrollParent as HTMLElement).getBoundingClientRect().left;
        const contentLeft =
            root?.getBoundingClientRect().left ?? Math.max((window.innerWidth - maxContentWidth) / 2, 0);
        const left = Math.max(VIEWPORT_GUTTER, viewportLeft + VIEWPORT_GUTTER);
        const availableWidth = Math.max(
            MIN_POPOVER_WIDTH,
            contentLeft - left - MARKER_WIDTH - MARKER_GAP - VIEWPORT_GUTTER,
        );
        const popoverWidth = Math.min(DEFAULT_POPOVER_WIDTH, availableWidth);

        setFixedPosition({ left, popoverWidth });
    }, [contentRef, maxContentWidth, placement]);

    useEffect(() => {
        const initialPositionFrame = window.requestAnimationFrame(updateFixedPosition);

        if (placement !== 'fixed') {
            return () => window.cancelAnimationFrame(initialPositionFrame);
        }

        const root = contentRef.current;
        const resizeObserver = root ? new ResizeObserver(updateFixedPosition) : undefined;

        resizeObserver?.observe(root as HTMLElement);
        window.addEventListener('resize', updateFixedPosition);

        return () => {
            window.cancelAnimationFrame(initialPositionFrame);
            resizeObserver?.disconnect();
            window.removeEventListener('resize', updateFixedPosition);
        };
    }, [contentRef, placement, updateFixedPosition]);

    useEffect(() => {
        refreshItems({ immediate: true });

        const root = contentRef.current;

        if (!root) {
            return undefined;
        }

        const observer = new MutationObserver(scheduleRefreshItems);

        observer.observe(root, {
            attributeFilter: [
                'data-memo-report-component-id',
                'data-memo-report-component-type',
                'data-memo-toc-title',
                'data-table-of-contents-hidden',
                'data-table-of-contents-label-ignore',
                'data-table-of-contents-title',
                'aria-hidden',
                'class',
                'hidden',
            ],
            attributes: true,
            characterData: true,
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();

            if (animationFrameRef.current) {
                window.cancelAnimationFrame(animationFrameRef.current);
            }

            if (refreshTimeoutRef.current) {
                window.clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, [contentRef, refreshItems, refreshKey, scheduleRefreshItems]);

    useEffect(() => {
        const root = contentRef.current;

        if (!root || !items.length) {
            return undefined;
        }

        const scrollParent = getScrollParent(root);

        const updateActiveItem = () => {
            const viewportTop = scrollParent === window ? 0 : (scrollParent as HTMLElement).getBoundingClientRect().top;
            const offset = 96;
            const activeItem =
                items
                    .map((item) => ({
                        item,
                        top: item.element.getBoundingClientRect().top - viewportTop,
                    }))
                    .filter(({ top }) => top <= offset)
                    .sort((a, b) => b.top - a.top)[0]?.item ?? items[0];

            setActiveItemId(activeItem?.id);
        };

        updateActiveItem();
        scrollParent.addEventListener('scroll', updateActiveItem, { passive: true });
        window.addEventListener('resize', updateActiveItem);

        return () => {
            scrollParent.removeEventListener('scroll', updateActiveItem);
            window.removeEventListener('resize', updateActiveItem);
        };
    }, [contentRef, items]);

    const scrollToItem = useCallback(
        (item: MemoTableOfContentsItem) => {
            onSelect?.();
            item.element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setActiveItemId(item.id);

            window.history.replaceState(null, '', `${window.location.pathname}#${item.id}`);
        },
        [onSelect],
    );

    const itemByKey = useMemo(() => new Map(items.map((item) => [item.key, item])), [items]);

    const renderIcon = useCallback(
        (item: MemoTableOfContentsItem) => {
            if (item.type === 'report-component') {
                const icon = item.reportComponentType
                    ? reportComponentTypeToIconMap[item.reportComponentType]
                    : undefined;

                return (
                    <Avatar
                        size={18}
                        shape='square'
                        icon={icon ?? <TbChartInfographic size='0.75rem' />}
                        style={{
                            border: `1px solid ${token.colorBorder}`,
                            background: token.colorBgContainer,
                            color: token.colorTextTertiary,
                        }}
                    />
                );
            }

            const headingIcon = getHeadingIcon?.(item);

            if (headingIcon) {
                return (
                    <Avatar
                        size={18}
                        shape='square'
                        icon={headingIcon}
                        style={{
                            border: `1px solid ${token.colorBorder}`,
                            background: token.colorBgContainer,
                            color: token.colorTextTertiary,
                        }}
                    />
                );
            }

            const HeadingIcon = headingIconMap[item.level] ?? TbH6;

            return (
                <Avatar
                    size={18}
                    shape='square'
                    icon={<HeadingIcon size='0.75rem' />}
                    style={{
                        border: `1px solid ${token.colorBorder}`,
                        background: token.colorBgContainer,
                        color: token.colorTextTertiary,
                    }}
                />
            );
        },
        [
            getHeadingIcon,
            reportComponentTypeToIconMap,
            token.colorBgContainer,
            token.colorBorder,
            token.colorTextTertiary,
        ],
    );

    const renderTitle = useCallback(
        (item: MemoTableOfContentsItem) => (
            <Row
                align='middle'
                wrap={false}
                gutter={8}
                onMouseEnter={() => setHoveredItemId(item.id)}
                onMouseLeave={() => setHoveredItemId(undefined)}
                style={{ minWidth: 0 }}
            >
                <Col flex='none'>{renderIcon(item)}</Col>
                <Col flex='auto' style={{ minWidth: 0 }}>
                    <Typography.Text
                        style={{
                            ...ellipsisStyling,
                            color: item.id === activeItemId ? token.colorText : token.colorTextSecondary,
                            display: 'block',
                            fontWeight: item.id === activeItemId ? 600 : 500,
                            maxWidth: 260,
                        }}
                    >
                        {item.title}
                    </Typography.Text>
                </Col>
            </Row>
        ),
        [activeItemId, renderIcon, token.colorText, token.colorTextSecondary],
    );

    const treeData = useMemo(() => {
        const roots: DataNode[] = [];
        const headingStack: Array<{ level: number; node: DataNode }> = [];

        items.forEach((item) => {
            const node: DataNode = {
                key: item.key,
                title: renderTitle(item),
                children: [],
            };

            if (item.type === 'report-component') {
                const parent = headingStack[headingStack.length - 1]?.node;

                if (parent) {
                    parent.children = [...(parent.children ?? []), node];
                } else {
                    roots.push(node);
                }

                return;
            }

            while (headingStack.length && headingStack[headingStack.length - 1].level >= item.level) {
                headingStack.pop();
            }

            const parent = headingStack[headingStack.length - 1]?.node;

            if (parent) {
                parent.children = [...(parent.children ?? []), node];
            } else {
                roots.push(node);
            }

            headingStack.push({ level: item.level, node });
        });

        return roots;
    }, [items, renderTitle]);

    const expandedKeys = useMemo(() => items.map((item) => item.key), [items]);

    if (placement !== 'drawer' && (!screens.lg || !items.length)) {
        return null;
    }

    const popoverContent = (
        <div
            style={{
                width: placement === 'drawer' ? '100%' : fixedPosition.popoverWidth,
                maxHeight: placement === 'drawer' ? 'calc(76dvh - 9rem)' : 420,
                overflow: 'auto',
                padding: placement === 'drawer' ? '0.25rem 0' : '0.5rem',
            }}
        >
            <small
                style={{
                    color: token.colorTextTertiary,
                    display: 'block',
                    fontWeight: 600,
                    margin: '0.25rem 0.5rem 0.5rem',
                }}
            >
                Table of Contents
            </small>
            <style>
                {`
                    .memo-table-of-contents-tree .ant-tree-switcher {
                        display: none;
                    }
                `}
            </style>
            {treeData.length ? (
                <Tree
                    blockNode
                    className='memo-table-of-contents-tree'
                    showIcon={false}
                    selectable
                    showLine
                    selectedKeys={activeItemId ? [activeItemId] : []}
                    expandedKeys={expandedKeys}
                    treeData={treeData}
                    onSelect={([key]) => {
                        const item = itemByKey.get(String(key));

                        if (item) {
                            scrollToItem(item);
                        }
                    }}
                    style={{
                        background: 'transparent',
                        color: token.colorText,
                    }}
                />
            ) : (
                <EmptySection
                    height={16 * 10}
                    icon={<TbListDetails size='1.25rem' />}
                    label='No Table of Contents'
                    description='Headings and report components will appear here.'
                />
            )}
        </div>
    );

    if (placement === 'drawer') {
        return popoverContent;
    }

    const wrapperStyle =
        placement === 'fixed'
            ? ({
                  left: fixedPosition.left,
                  position: 'fixed',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 12,
              } as const)
            : ({
                  marginLeft: '0.75rem',
                  position: 'sticky',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 1,
              } as const);

    return (
        <div style={wrapperStyle}>
            <Popover
                arrow={false}
                content={popoverContent}
                mouseEnterDelay={0.1}
                mouseLeaveDelay={0.15}
                placement='leftTop'
                styles={{
                    body: {
                        background: token.colorBgElevated,
                        border: `1px solid ${token.colorBorder}`,
                        borderRadius: token.borderRadiusLG,
                        boxShadow: token.boxShadowSecondary,
                        padding: 0,
                    },
                    root: {
                        marginLeft: '1.5rem',
                    },
                }}
                trigger={['hover', 'focus']}
            >
                <Flex
                    vertical
                    align='flex-start'
                    aria-label='Open table of contents'
                    data-table-of-contents-trigger='true'
                    gap={5}
                    role='button'
                    tabIndex={0}
                    style={{
                        boxSizing: 'border-box',
                        cursor: 'pointer',
                        flex: 'none',
                        padding: '0.375rem 0.25rem',
                        width: TRIGGER_WIDTH,
                    }}
                >
                    {items.slice(0, 24).map((item) => {
                        const active = item.id === activeItemId;
                        const hovered = item.id === hoveredItemId;
                        const markerWidth =
                            item.type === 'report-component' ? 4 : Math.max(7, 18 - Math.max(item.level - 1, 0) * 2);

                        return (
                            <div
                                key={item.id}
                                style={{
                                    width: hovered ? markerWidth + 6 : markerWidth,
                                    height: item.type === 'report-component' ? (hovered ? 5 : 4) : hovered ? 3 : 2,
                                    marginLeft: item.type === 'report-component' ? 7 : Math.max(0, item.level - 1) * 2,
                                    borderRadius: 999,
                                    background: active || hovered ? token.colorText : token.colorTextQuaternary,
                                    opacity: active || hovered ? 1 : 0.72,
                                    transform: hovered ? 'translateX(2px)' : undefined,
                                    transition:
                                        'background 160ms ease, height 160ms ease, opacity 160ms ease, transform 160ms ease, width 160ms ease',
                                }}
                            />
                        );
                    })}
                </Flex>
            </Popover>
        </div>
    );
};

export default MemoTableOfContents;
